import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Dropdown,
  Spinner,
  InputGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSearch,
  faSort,
  faDownload,
  faEye,
  faEdit,
  faTrash,
  faEllipsisV,
  faSync,
  faCheck
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams, useHistory } from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal'
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary'
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert'
import * as GrnController from '../Controller/GrnController'
import * as InvoiceController from '../../Invoice/Controller/InvoiceController'
import '../Assets/css/grn.scss'

const GrnList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState('id')
  const [sortDirection, setSortDirection] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [selectedGrnSlack, setSelectedGrnSlack] = useState(null)
  const [selectedRange, setSelectedRange] = useState('all')
  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    sort: [
      {
        field: 'id',
        sort: 'desc'
      }
    ],
    search: '',
    from_created_date: '',
    to_created_date: ''
  })
  const suppressNextFetchRef = useRef(false)

  const setQuickRange = (type) => {
    const today = new Date()
    let from = ''
    let to = ''
    const fmt = (d) => d.toISOString().split('T')[0]
    switch (type) {
      case 'today':
        from = fmt(today)
        to = fmt(today)
        break
      case 'yesterday': {
        const y = new Date(today)
        y.setDate(y.getDate() - 1)
        from = fmt(y)
        to = fmt(y)
        break
      }
      case 'last7': {
        const f = new Date(today)
        f.setDate(f.getDate() - 6)
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'lastMonth': {
        const f = new Date(today)
        f.setDate(f.getDate() - 29)
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'all':
        from = ''
        to = ''
        break
      default:
        break
    }
    setFilters((prev) => ({
      ...prev,
      from_created_date: from,
      to_created_date: to,
      offset: 0
    }))
    setSelectedRange(type)
  }

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }))
  }

  const handleQuickRangeClick = (e) => {
    const range = e.currentTarget.getAttribute('data-range')
    if (range === 'custom') {
      setSelectedRange('custom')
      return
    }
    if (range === 'today') return setQuickRange('today')
    if (range === 'yesterday') return setQuickRange('yesterday')
    if (range === 'last7') return setQuickRange('last7')
    if (range === 'lastMonth') return setQuickRange('lastMonth')
    if (range === 'all') return setQuickRange('all')
  }

  const handleRefresh = () => {
    performFetch(filters)
  }

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        setLoading(true)
        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
        }
        const response = await GrnController.getGrnsApi(effectiveFilters)
        if (response.success) {
          const r = response.data || []
          setRows(r)
          setTotalItems(response.total || r.length)
        } else {
          setRows([])
        }
      } catch (error) {
        console.error('Error fetching GRNs:', error)
        setRows([])
      } finally {
        setLoading(false)
      }
    },
    [props.userEntryType]
  )

  const fetchRows = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    fetchRows()
  }, [fetchRows])

  useEffect(() => {
    if (
      selectedRange === 'custom' &&
      filters.from_created_date &&
      filters.to_created_date
    ) {
      performFetch({ ...filters })
    }
  }, [selectedRange, filters, performFetch])

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) {
      performFetch(next)
    }
  }

  const startIndex = totalItems === 0 ? 0 : filters.offset + 1
  const endIndex =
    totalItems === 0 ? 0 : Math.min(filters.offset + rows.length, totalItems)

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }))
  }

  const handleSort = (field) => {
    let newSortDirection = 'asc'
    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      setSortField(field)
    }
    setSortDirection(newSortDirection)
    setFilters((prev) => ({
      ...prev,
      sort: [
        {
          field: field,
          sort: newSortDirection
        }
      ]
    }))
  }

  const handleDelete = async (slack) => {
    if (!window.confirm('Are you sure you want to delete this GRN?')) return
    try {
      setDeleting(true)
      const response = await GrnController.deleteGrnApi(slack)
      if (response.success) {
        performFetch(filters)
      } else {
        console.error('Failed to delete GRN:', response.message)
      }
    } catch (error) {
      console.error('Error deleting GRN:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleMakeCompleted = (grnSlack) => {
    setSelectedGrnSlack(grnSlack)
    setShowCompleteModal(true)
  }

  const confirmMakeCompleted = async () => {
    setShowCompleteModal(false)
    
    if (!selectedGrnSlack) return

    try {
      setDeleting(true)
      
      // First get invoice data from GRN
      const invoiceResponse = await GrnController.getGrnInvoiceDataApi(selectedGrnSlack)
      if (!invoiceResponse.success) {
        Utility.toastNotifications(invoiceResponse.message || 'Failed to get invoice data from GRN', 'Error', 'error')
        return
      }

      // Transform GRN data to invoice payload structure
      const invoicePayload = {
        bill_to: invoiceResponse.data.bill_to,
        bill_to_slack: invoiceResponse.data.bill_to_slack,
        invoice_reference: invoiceResponse.data.invoice_reference,
        invoice_date: invoiceResponse.data.invoice_date,
        invoice_due_date: invoiceResponse.data.invoice_date, // Default to invoice date if not specified
        currency: invoiceResponse.data.currency,
        shipping_charge: parseFloat(invoiceResponse.data.shipping_charge || 0),
        packing_charge: parseFloat(invoiceResponse.data.packing_charge || 0),
        terms: '', // Default empty if not provided
        tax_option: invoiceResponse.data.tax_option,
        products: invoiceResponse.data.products.map(product => ({
          slack: product.slack,
          name: product.name,
          quantity: product.quantity,
          unit_price: parseFloat(product.unit_price),
          discount_percentage: parseFloat(product.discount_percentage || 0),
          tax_type: product.tax_type,
          tax_percentage: parseFloat(product.tax_percentage),
          amount: parseFloat(product.amount)
        })),
        store_id: String(invoiceResponse.data.store_id),
        invoice_from_grn: true,
        grn_slack: selectedGrnSlack
      }

      // Call createInvoice API directly
      const invoiceCreateResponse = await InvoiceController.createInvoiceApi(invoicePayload)
      
      if (invoiceCreateResponse.success) {
        Utility.toastNotifications('Invoice created successfully from GRN', 'Success', 'success')
        
        // Mark GRN as completed
        const grnResponse = await GrnController.makeCompletedApi(selectedGrnSlack)
        if (grnResponse.success) {
          Utility.toastNotifications('GRN marked as completed successfully', 'Success', 'success')
        } else {
          Utility.toastNotifications('Failed to mark GRN as completed', 'Error', 'error')
        }
        
        // Refresh the GRN list
        performFetch()
      } else {
        Utility.toastNotifications(invoiceCreateResponse.message || 'Failed to create invoice', 'Error', 'error')
      }
    } catch (error) {
      console.error('Error marking GRN as completed:', error)
      Utility.toastNotifications('Failed to process GRN completion', 'Error', 'error')
    } finally {
      setDeleting(false)
      setSelectedGrnSlack(null)
    }
  }

  const closeCompleteModal = () => {
    setShowCompleteModal(false)
    setSelectedGrnSlack(null)
  }

  const handleMenuAction = (action, grnSlack) => {
    // Handle any additional menu options that might be added in the future
    console.log('Menu action:', action, 'GRN:', grnSlack)
    
    switch (action) {
      // Add cases for future menu options here
      default:
        Utility.toastNotifications(`${action} functionality not yet implemented`, 'Info', 'info')
        break
    }
  }

  
  const getStatusBadge = (status) => {
    let raw = status
    if (raw && typeof raw === 'object') {
      raw = raw.value_constant || raw.value || raw.status || raw.label
    }
    const key = typeof raw === 'string' ? raw.toUpperCase() : raw
    const map = {
      0: { label: 'Draft', color: '#6c757d' },
      1: { label: 'Pending', color: '#fd7e14' },
      2: { label: 'Posted', color: '#198754' },
      3: { label: 'Rejected Lines', color: '#dc3545' },
      4: { label: 'Ready to Post', color: '#0d6efd' },
      DRAFT: { label: 'Draft', color: '#6c757d' },
      PENDING: { label: 'Pending', color: '#fd7e14' },
      POSTED: { label: 'Posted', color: '#198754' },
      REJECTED: { label: 'Rejected Lines', color: '#dc3545' },
      READY_TO_POST: { label: 'Ready to Post', color: '#0d6efd' }
    }
    const info = map[key] || { label: String(raw ?? '-'), color: '#6c757d' }
    return <span style={{ color: info.color, fontWeight: 500 }}>{info.label}</span>
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const grnViewPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'grn',
    ['create'],
    props.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Goods Receipt Notes (GRN)</h4>
        <div>
          {/* <Button
            variant='outline-secondary'
            className='me-2'
            onClick={useCallback(async () => {
              try {
                setLoading(true)
                const exportData = {
                  from_created_date: filters.from_created_date,
                  to_created_date: filters.to_created_date
                }
                const response = await GrnController.exportGrnsApi(exportData)
                if (response && response.success && response.data) {
                  if (response.isFileUrl) {
                    await Utility.downloadFile(
                      response.data,
                      response.filename || 'grn_export.xlsx'
                    )
                  } else {
                    const blob = new Blob([response.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    })
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = response.filename || 'grn_export.xlsx'
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                  }
                }
              } catch (e) {
                console.error('Export GRNs failed:', e)
              } finally {
                setLoading(false)
              }
            }, [filters])}
          >
            <FontAwesomeIcon icon={faDownload} className='me-2' /> Download
          </Button> */}
          {grnViewPermissions.create && (
          <Button
            variant='primary'
            onClick={() => history.push(`/${lng}/sales/grn/add`)}
          >
            <FontAwesomeIcon icon={faPlus} className='me-2' /> New GRN
          </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <Row className='mb-3 gx-2 searchBarPanel'>
            <Col className='d-flex align-items-center flex-wrap gap-2 innSearchBarPanel'>
              {selectedRange === 'custom' && (
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    value={filters.from_created_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        from_created_date: e.target.value,
                        offset: 0
                      }))
                    }
                    style={{ width: 160 }}
                  />
                  <span>to</span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    min={filters.from_created_date || undefined}
                    value={filters.to_created_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        to_created_date: e.target.value,
                        offset: 0
                      }))
                    }
                    style={{ width: 160 }}
                  />
                </div>
              )}
              <div className='btn-group ms-auto'>
                <Button
                  size='sm'
                  variant={selectedRange === 'today' ? 'primary' : 'light'}
                  data-range='today'
                  onClick={handleQuickRangeClick}
                >
                  Today
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'yesterday' ? 'primary' : 'light'}
                  data-range='yesterday'
                  onClick={handleQuickRangeClick}
                >
                  Yesterday
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'last7' ? 'primary' : 'light'}
                  data-range='last7'
                  onClick={handleQuickRangeClick}
                >
                  Last 7 days
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'lastMonth' ? 'primary' : 'light'}
                  data-range='lastMonth'
                  onClick={handleQuickRangeClick}
                >
                  Last Month
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'custom' ? 'primary' : 'light'}
                  data-range='custom'
                  onClick={handleQuickRangeClick}
                >
                  Custom Dates
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'all' ? 'primary' : 'light'}
                  data-range='all'
                  onClick={handleQuickRangeClick}
                >
                  All
                </Button>
              </div>
              <div className='searchFieldBoxes'>
                <div className='ms-2 searchFieldBox'>
                  <InputGroup size='sm'>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <DebounceInput
                      minLength={1}
                      debounceTimeout={500}
                      className='form-control'
                      placeholder='Search by GRN, PO, Invoice'
                      value={searchTerm || ''}
                      onChange={(e) => handleSearch(e)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault()
                      }}
                    />
                  </InputGroup>
                </div>
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={loading}
                    title='Refresh'
                  >
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th onClick={() => handleSort('grn_number')} style={{ cursor: 'pointer' }}>
                    GRN # {sortField === 'grn_number' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('grn_date')} style={{ cursor: 'pointer' }}>
                    GRN DATE {sortField === 'grn_date' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('invoice_number')} style={{ cursor: 'pointer' }}>
                    INVOICE # {sortField === 'invoice_number' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('supplier_name')} style={{ cursor: 'pointer' }}>
                    SUPPLIER {sortField === 'supplier_name' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('purchase_order.po_number')} style={{ cursor: 'pointer' }}>
                    PURCHASE ORDER {sortField === 'purchase_order.po_number' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('products_count')} style={{ cursor: 'pointer' }}>
                    PRODUCTS {sortField === 'products_count' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('total_amount')} style={{ cursor: 'pointer' }}>
                    TOTAL AMOUNT {sortField === 'total_amount' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th onClick={() => handleSort('status_data.label')} style={{ cursor: 'pointer', minWidth: 160 }}>
                    STATUS {sortField === 'status_data.label' && <FontAwesomeIcon icon={faSort} size='xs' />}
                  </th>
                  <th style={{ width: 48 }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan='9' className='text-center py-4'>
                      <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading GRNs...</span>
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((r) => (
                    <tr 
                      key={r.id}
                      onDoubleClick={() => history.push(`/${lng}/sales/grn/view/${r.slack}`)}
                      className='cursor-pointer'
                    >
                      <td>
                        <Link to={`/${lng}/sales/grn/view/${r.slack}`} className='text-primary fw-medium text-decoration-none'>
                          {r.grn_number || '-'}
                        </Link>
                      </td>
                      <td>{r.grn_date || '-'}</td>
                      <td>{r.invoice_number || '-'}</td>
                      <td>{r.supplier_name || '-'}</td>
                      <td>
                        {r.purchase_order ? (
                          <div>
                            <div className='fw-medium'>{r.purchase_order.po_number || '-'}</div>
                            {r.purchase_order.po_date && (
                              <div className='text-muted small'>{r.purchase_order.po_date}</div>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td>
                        {r.products && r.products.length > 0 ? (
                          r.products.length === 1 ? (
                            // <div className='text-truncate' style={{ maxWidth: '150px' }}>
                              r.products[0].product_name + ', Qty - ' + r.products[0].received_quantity + ' ' + r.products[0]?.Product?.unit_of_measure
                            // </div>
                          ) : (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip>
                                  {r.products.map((product, index) => (
                                    <div key={index}>
                                      {product.product_name}, Qty-{product.received_quantity} {product?.Product?.unit_of_measure}
                                    </div>
                                  ))}
                                </Tooltip>
                              }
                            >
                              <Badge bg='info' className='rounded-pill' style={{ cursor: 'pointer' }}>
                                {r.products_count || 0} Products
                              </Badge>
                            </OverlayTrigger>
                          )
                        ) : (
                          <Badge bg='secondary' className='rounded-pill'>
                            0 Products
                          </Badge>
                        )}
                      </td>
                      <td>
                        <span className='fw-medium'>
                          {r?.total_amount_actual ? Utility.formatCurrencyValue(parseFloat(r.total_amount_actual).toFixed(2)) : '-'}
                        </span>
                      </td>
                      <td>
                        {/* <Badge 
                          bg={
                            r.status_data?.label === 'Quality Check Pending' ? 'warning' :
                            r.status_data?.label === 'Partially Received' ? 'info' :
                            r.status_data?.label === 'Closed' ? 'success' :
                            r.status_data?.label === 'Approved' ? 'success' :
                            r.status_data?.label === 'Rejected' ? 'danger' : 'secondary'
                          }
                          className='rounded-pill'
                        >
                          {r.status_data?.label || r.quality_check_status || '-'}
                        </Badge> */}
                        {Utility.getStatusDataBadge(r.status_data)}
                      </td>
                      <td>
                        <Dropdown align='end'>
                          <Dropdown.Toggle
                            variant='light'
                            size='sm'
                            id={`dropdown-${r.id}`}
                            className='btn-icon no-caret'
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {/* Dynamic menu options from API (excluding "Perform Quality Check") */}
                            {r.menu_options && r.menu_options.length > 0 && (
                              <>
                                {r.menu_options.map((option, index) => {
                                  // Skip "Perform Quality Check"
                                  if (option === 'Perform Quality Check') return null
                                  
                                  // Handle different menu options
                                  if (option === 'View') {
                                    return (
                                      <Dropdown.Item 
                                        key={index}
                                        as={Link} 
                                        to={`/${lng}/sales/grn/view/${r.slack}`}
                                      >
                                        <FontAwesomeIcon icon={faEye} className='me-2' /> View
                                      </Dropdown.Item>
                                    )
                                  } else if (option === 'Complete GRN') {
                                    return (
                                      <Dropdown.Item  
                                        key={index}
                                        className='text-success'
                                        onClick={() => handleMakeCompleted(r.slack)}
                                      >
                                        <FontAwesomeIcon icon={faCheck} className='me-2' /> Complete GRN
                                      </Dropdown.Item>
                                    )
                                  } else if (option === 'Delete GRN') {
                                    return (
                                      <Dropdown.Item
                                        key={index}
                                        className='text-danger'
                                        onClick={() => handleDelete(r.slack)}
                                        disabled={deleting}
                                      >
                                        <FontAwesomeIcon icon={faTrash} className='me-2' /> Delete GRN
                                      </Dropdown.Item>
                                    )
                                  } else {
                                    // Handle any other menu options
                                    return (
                                      <Dropdown.Item key={index} onClick={() => handleMenuAction(option, r.slack)}>
                                        {option}
                                      </Dropdown.Item>
                                    )
                                  }
                                })}
                              </>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='9' className='text-center py-4 nosearchResult'>
                      {searchTerm ? 'No GRNs found matching your search.' : 'No GRNs found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Confirmation Modal for Complete GRN */}
      <ErrorBoundary title="ModalGlobal Error">
        <ModalGlobal
          show={showCompleteModal}
          onHide={closeCompleteModal}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Complete GRN"
          footer={false}
          body={
            <>
              <ErrorBoundary title="ConfirmationAlert Error">
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to mark this GRN as completed and create invoice?"
                  BodySecondContent=""
                  BodyThirdContent=""
                  confirmationButtonContent="Yes"
                  cancelButtonContent="No"
                  deleteConfirmButton={confirmMakeCompleted}
                  deleteCancleButton={closeCompleteModal}
                />
              </ErrorBoundary>
            </>
          }
        />
      </ErrorBoundary>
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType
})

export default connect(mapStateToProps)(withNamespaces()(GrnList))
